import { trackConversion } from "@/lib/analytics/gtag";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { PageShell, PageHero } from "@/components/PageShell";
import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/contact")({
  component: Contact,
  head: () => ({
    meta: [
      { title: "Contact N S Physiotherapy | Centurion Physio Clinic" },
      { name: "description", content: "Contact N S Physiotherapy in Centurion. WhatsApp, email or visit our Eco Park clinic. Same day appointments and medical aid accepted." },
      { property: "og:title", content: "Contact N S Physiotherapy | Centurion Physio Clinic" },
      { property: "og:description", content: "Get in touch with your Centurion physiotherapist. Same day appointments available." },
      { property: "og:url", content: "https://nsphysiotherapy.co.za/contact" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://nsphysiotherapy.co.za/contact" }],
  }),
});

function Contact() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const get = (id: string) => (form.querySelector(`#${id}`) as HTMLInputElement | HTMLTextAreaElement | null)?.value.trim() ?? "";
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/public/enquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: get("contact-name"),
          phone: get("contact-phone"),
          email: get("contact-email"),
          message: get("contact-message"),
          source: "contact page",
        }),
      });
      if (!res.ok) throw new Error("failed");
      trackConversion("contact", "contact_form_submitted", { form_name: "contact" });
      setSent(true);
      form.reset();
    } catch {
      setError("We could not send your message. Please WhatsApp us instead.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageShell>
      <PageHero eyebrow="We'd love to hear from you" title="Contact N S Physiotherapy" subtitle="Better yet, see us in person. Visit during business hours by appointment." />

      <section className="container-page py-16 grid gap-8 md:grid-cols-[1.1fr_1fr]">
        <form onSubmit={handleSubmit} className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-4">
          <h2 className="font-display text-2xl font-bold text-primary">Send us a message</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-1.5 text-sm" htmlFor="contact-name">Name<input id="contact-name" required className="rounded-md border border-input bg-background px-3 py-2" /></label>
            <label className="grid gap-1.5 text-sm" htmlFor="contact-phone">Phone<input id="contact-phone" required type="tel" className="rounded-md border border-input bg-background px-3 py-2" /></label>
          </div>
          <label className="grid gap-1.5 text-sm" htmlFor="contact-email">Email<input id="contact-email" required type="email" className="rounded-md border border-input bg-background px-3 py-2" /></label>
          <label className="grid gap-1.5 text-sm" htmlFor="contact-message">How can we help?<textarea id="contact-message" required rows={5} className="rounded-md border border-input bg-background px-3 py-2" /></label>
          <button disabled={busy} className="btn-hero inline-flex h-11 items-center rounded-full px-6 font-semibold disabled:opacity-60">{busy ? "Sending..." : "Send message"}</button>
          {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
          {sent && <div className="rounded-md bg-secondary p-3 text-sm text-primary">Thanks, we'll be in touch shortly.</div>}
        </form>


        <div className="space-y-5">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold text-primary">Centurion</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex gap-2"><MapPin className="h-4 w-4 mt-0.5 text-primary" /> {SITE.addressCenturion}</li>
              <li className="flex gap-2"><Clock className="h-4 w-4 mt-0.5 text-primary" /> Open daily · 07:00 – 20:00</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-bold text-primary">Home Visits</h3>
            <p className="mt-3 text-sm text-foreground/75">Mobile physiotherapy across Centurion, Midrand, Pretoria and Johannesburg, treatment in the comfort of your home or workplace.</p>
          </div>
          <div className="rounded-2xl border border-border bg-secondary/60 p-6">
            <div className="grid gap-2 text-sm">
              <a className="flex items-center gap-2 text-primary hover:underline" href={`tel:${SITE.phoneRaw}`}><Phone className="h-4 w-4" /> {SITE.phone}</a>
              <a className="flex items-center gap-2 text-primary hover:underline" href={SITE.whatsapp} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /> WhatsApp us</a>
              <a className="flex items-center gap-2 text-primary hover:underline" href={`mailto:${SITE.email}`}><Mail className="h-4 w-4" /> {SITE.email}</a>
            </div>
          </div>
        </div>
      </section>

      <section className="container-page pb-16">
        <div className="rounded-3xl overflow-hidden border border-border">
          <iframe title="Map" src="https://www.google.com/maps?q=270+Witch+Hazel+Avenue,+Eco+Park+Estate,+Centurion&output=embed" className="h-[380px] w-full" loading="lazy" />
        </div>
      </section>
    </PageShell>
  );
}
